/**
 * The optional "Correct answer", always there like Comment: write it as free
 * text, or switch to JSON for a structured answer. "Agent's answer" and
 * "Expected output" are shortcuts that fill the box to edit from (Langfuse
 * corrections, LangSmith corrected examples) — never required. A structured
 * answer shows its fields (× drops one, "Add field" adds one); switching mode
 * keeps what was written in the other. `value` is the corrected answer
 * itself; `null` means none.
 */

import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LabelMark } from "@/components/ui/field";
import { Segmented } from "@/components/ui/tabs";
import { FieldsEditor } from "./corrected-fields";
import { isObject } from "./scores";
import { JsonBox } from "./value-editors";

type Mode = "text" | "json";

const modeOf = (v: unknown): Mode => (v === null || typeof v === "string" ? "text" : "json");

export function CorrectedOutput({
  value,
  onChange,
  agentOutput,
  expectedOutput,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  agentOutput: unknown;
  /** The dataset's expected output, offered as a second shortcut. */
  expectedOutput?: unknown;
}) {
  const [mode, setMode] = useState<Mode>(modeOf(value));
  // What was written in the other mode, back on switching to it.
  const [other, setOther] = useState<unknown>(null);
  // Bumped when a shortcut or Clear replaces the answer, so the boxes re-read it.
  const [fills, setFills] = useState(0);
  const put = (next: unknown) => {
    onChange(next);
    setMode(modeOf(next));
    setOther(null);
    setFills((n) => n + 1);
  };
  const switchTo = (next: Mode) => {
    if (next === mode) return;
    onChange(other);
    setOther(value);
    setMode(next);
    setFills((n) => n + 1);
  };
  const shortcuts = [
    { label: "Agent's answer", value: agentOutput },
    { label: "Expected output", value: expectedOutput },
  ].filter((s) => s.value !== null && s.value !== undefined);

  return (
    <section aria-label="Correct answer" className="flex flex-col gap-2 border-t border-line-soft pt-4">
      <div className="flex items-center gap-2">
        <h3 className="m-0 flex-1 text-[13px] font-semibold">
          Correct answer
          <LabelMark optional />
        </h3>
        <Segmented
          label="Answer as"
          value={mode}
          onChange={switchTo}
          items={[
            { key: "text", label: "Free text" },
            { key: "json", label: "JSON" },
          ]}
        />
      </div>
      {mode === "text" ? (
        <>
          <label htmlFor="corrected-text" className="sr-only">
            Correct answer in words
          </label>
          <textarea
            key={fills}
            id="corrected-text"
            className="inp field-sizing-content max-h-72 min-h-[72px]! text-[13px] leading-relaxed"
            placeholder="What the agent should have answered, if it got it wrong."
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          />
        </>
      ) : isObject(value) ? (
        <FieldsEditor
          key={fills}
          value={value}
          original={isObject(agentOutput) ? agentOutput : {}}
          onChange={onChange}
        />
      ) : (
        <>
          <label htmlFor="corrected-json" className="sr-only">
            Correct answer as JSON
          </label>
          <JsonBox
            key={fills}
            id="corrected-json"
            value={value}
            placeholder='{"result": "exclude"}'
            onChange={onChange}
          />
        </>
      )}
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
        {shortcuts.length > 0 && <span>Fill with:</span>}
        {shortcuts.map((s) => (
          <Button key={s.label} size="sm" onClick={() => put(structuredClone(s.value))}>
            {s.label}
          </Button>
        ))}
        <span className="flex-1" />
        {value !== null && (
          <Button size="sm" onClick={() => put(null)}>
            <X size={12} strokeWidth={2.2} aria-hidden />
            Clear
          </Button>
        )}
      </div>
    </section>
  );
}
